import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link, useParams } from "react-router-dom";

import { Avatar, PriorityBadge, StatusChip, TicketKey } from "@m2s/design-system";

import { useDirectory } from "@/hooks/useDirectory";
import { api } from "@/lib/api";
import type { CommitLink, Issue, IssueComment, IssueLinks, PrLink } from "@/lib/types";

const STATUS_LABELS: Record<Issue["status"], string> = {
  todo: "To do",
  in_progress: "In progress",
  in_review: "In review",
  done: "Done",
  blocked: "Blocked",
};

export function IssueDetailPage() {
  const { issueKey } = useParams<{ issueKey: string }>();
  const queryClient = useQueryClient();
  const key = (issueKey ?? "").toUpperCase();

  const { data: issue, isLoading } = useQuery({
    queryKey: ["issue", key],
    queryFn: async () => (await api.get<Issue>(`/issues/${key}`)).data,
    enabled: !!key,
  });

  const { data: comments = [] } = useQuery({
    queryKey: ["issue-comments", key],
    queryFn: async () => (await api.get<IssueComment[]>(`/issues/${key}/comments`)).data,
    enabled: !!key,
  });

  const { data: links } = useQuery({
    queryKey: ["issue-links", key],
    queryFn: async () => (await api.get<IssueLinks>(`/issues/${key}/links`)).data,
    enabled: !!key,
  });

  // Name-only roster — resolves assignee / reporter / comment-author names for
  // everyone (employees included; `/admin/members` 403s for non-admins).
  const { byId: memberById } = useDirectory();

  const [draft, setDraft] = useState("");
  const addComment = useMutation({
    mutationFn: async (body: string) =>
      (await api.post(`/issues/${key}/comments`, { body_md: body })).data,
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: ["issue-comments", key] });
    },
  });

  const transition = useMutation({
    mutationFn: async (status: Issue["status"]) =>
      (await api.post(`/issues/${key}/transition`, { status })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issue", key] });
      queryClient.invalidateQueries({ queryKey: ["issues"] }); // any board view
    },
  });

  if (isLoading || !issue) return <p style={{ color: "var(--mute)" }}>Loading…</p>;

  const assignee = issue.assignee_id ? memberById.get(issue.assignee_id) : null;
  const reporter = memberById.get(issue.reporter_id);

  return (
    <article style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 32 }}>
      <div>
        <nav style={{ marginBottom: 12, fontSize: 12, color: "var(--mute)" }}>
          <Link to="/board/APO" style={{ color: "inherit" }}>
            Board
          </Link>
          {" / "}
          <TicketKey value={issue.key} />
        </nav>
        <h1 style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-0.4px", margin: "4px 0 16px" }}>
          {issue.title}
        </h1>

        <section
          style={{
            background: "var(--canvas)",
            border: "1px solid var(--hairline)",
            borderRadius: 6,
            padding: 16,
            marginBottom: 24,
            fontSize: 14,
            lineHeight: 1.65,
            color: "var(--body)",
          }}
          className="m2s-markdown"
        >
          {issue.description_md ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{issue.description_md}</ReactMarkdown>
          ) : (
            <span style={{ color: "var(--mute)" }}>No description.</span>
          )}
        </section>

        {(links && (links.prs.length > 0 || links.commits.length > 0)) && (
          <LinkedDevSection prs={links.prs} commits={links.commits} />
        )}

        <h2 style={{ fontSize: 14, fontWeight: 500, margin: "24px 0 12px" }}>
          Activity · {comments.length}
        </h2>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {comments.map((c) => {
            const author = memberById.get(c.author_id);
            return (
              <li
                key={c.id}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "12px 0",
                  borderTop: "1px solid var(--hairline)",
                }}
              >
                <Avatar
                  initials={initialsOf(author?.display_name ?? "??")}
                  title={author?.display_name}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: "var(--mute)" }}>
                    {author?.display_name ?? "Unknown"} ·{" "}
                    <time>{new Date(c.created_at).toLocaleString()}</time>
                  </div>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: 13.5,
                      lineHeight: 1.55,
                      marginTop: 4,
                    }}
                  >
                    {c.body_md}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.trim()) addComment.mutate(draft.trim());
          }}
          style={{ marginTop: 16 }}
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a comment…"
            rows={3}
            style={{
              width: "100%",
              background: "var(--canvas)",
              border: "1px solid var(--hairline)",
              borderRadius: 4,
              padding: 10,
              fontFamily: "var(--font-sans)",
              fontSize: 13.5,
              color: "var(--ink)",
              resize: "vertical",
              outline: "none",
            }}
          />
          <button
            type="submit"
            disabled={!draft.trim() || addComment.isPending}
            style={{
              marginTop: 8,
              background: "var(--ink)",
              color: "var(--on-primary)",
              border: "none",
              borderRadius: 4,
              padding: "8px 14px",
              fontSize: 13,
              fontWeight: 500,
              cursor: draft.trim() ? "pointer" : "not-allowed",
              opacity: draft.trim() ? 1 : 0.5,
            }}
          >
            Comment
          </button>
        </form>
      </div>

      <aside>
        <Rail label="Status">
          <select
            value={issue.status}
            onChange={(e) => transition.mutate(e.target.value as Issue["status"])}
            style={selectStyle}
          >
            {(Object.keys(STATUS_LABELS) as Issue["status"][]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </Rail>
        <Rail label="Priority">
          <PriorityBadge priority={issue.priority} />
        </Rail>
        <Rail label="Estimate">
          {issue.estimate_points != null ? (
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
              {issue.estimate_points} pt
            </span>
          ) : (
            <span style={{ color: "var(--mute)", fontSize: 12 }}>—</span>
          )}
        </Rail>
        <Rail label="Assignee">
          {assignee ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Avatar initials={initialsOf(assignee.display_name)} size={20} />
              <span style={{ fontSize: 13 }}>{assignee.display_name}</span>
            </span>
          ) : (
            <span style={{ color: "var(--mute)", fontSize: 12 }}>Unassigned</span>
          )}
        </Rail>
        <Rail label="Reporter">
          {reporter ? (
            <span style={{ fontSize: 13 }}>{reporter.display_name}</span>
          ) : (
            <span style={{ color: "var(--mute)", fontSize: 12 }}>—</span>
          )}
        </Rail>
        <Rail label="Labels">
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {issue.labels.length === 0 ? (
              <span style={{ color: "var(--mute)", fontSize: 12 }}>None</span>
            ) : (
              issue.labels.map((l) => (
                <span
                  key={l}
                  style={{
                    fontSize: 11,
                    padding: "1px 6px",
                    background: "var(--surface-2)",
                    borderRadius: 2,
                    color: "var(--body-mid)",
                  }}
                >
                  {l}
                </span>
              ))
            )}
          </div>
        </Rail>
        <Rail label="State">
          <StatusChip status={issue.status} />
        </Rail>
      </aside>
    </article>
  );
}

function initialsOf(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function LinkedDevSection({ prs, commits }: { prs: PrLink[]; commits: CommitLink[] }) {
  return (
    <section style={{ marginTop: 24 }}>
      <h2 style={{ fontSize: 14, fontWeight: 500, margin: "0 0 12px" }}>
        Development · {prs.length} PR{prs.length === 1 ? "" : "s"} ·{" "}
        {commits.length} commit{commits.length === 1 ? "" : "s"}
      </h2>
      {prs.length > 0 && (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {prs.map((pr) => (
            <li
              key={pr.id}
              style={{
                borderTop: "1px solid var(--hairline)",
                padding: "10px 0",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span style={prStatePill(pr.state)}>{pr.state}</span>
              <a
                href={pr.github_url}
                target="_blank"
                rel="noreferrer"
                style={{ flex: 1, fontSize: 13.5, color: "var(--ink)", textDecoration: "none" }}
              >
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--mute)" }}>
                  #{pr.pr_number}
                </span>{" "}
                {pr.title}
              </a>
              <span style={{ fontSize: 11, color: "var(--mute)", fontFamily: "var(--font-mono)" }}>
                {pr.repo_full_name} · {pr.branch}
              </span>
            </li>
          ))}
        </ul>
      )}
      {commits.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.6, marginTop: 16, marginBottom: 6 }}>
            Commits
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {commits.slice(0, 8).map((c) => (
              <li
                key={c.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 0",
                  fontSize: 13,
                }}
              >
                <a
                  href={c.github_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11.5,
                    background: "var(--surface-2)",
                    color: "var(--ink)",
                    padding: "1px 6px",
                    borderRadius: 3,
                    textDecoration: "none",
                    flexShrink: 0,
                  }}
                >
                  {c.short_sha}
                </a>
                <span
                  style={{
                    flex: 1,
                    color: "var(--body)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.message}
                </span>
                {c.author_login && (
                  <span style={{ fontSize: 11, color: "var(--mute)" }}>@{c.author_login}</span>
                )}
              </li>
            ))}
          </ul>
          {commits.length > 8 && (
            <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 4 }}>
              … and {commits.length - 8} more.
            </div>
          )}
        </>
      )}
    </section>
  );
}

function prStatePill(state: "open" | "closed" | "merged"): React.CSSProperties {
  const map = {
    open: { bg: "rgba(0,215,34,0.12)", fg: "#047a18" },
    merged: { bg: "rgba(122,61,255,0.12)", fg: "#5a26d6" },
    closed: { bg: "rgba(238,29,54,0.10)", fg: "#ee1d36" },
  } as const;
  const s = map[state] ?? map.open;
  return {
    fontSize: 10.5,
    fontWeight: 500,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    padding: "2px 8px",
    borderRadius: 3,
    background: s.bg,
    color: s.fg,
    flexShrink: 0,
  };
}

const selectStyle: React.CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "5px 8px",
  fontFamily: "var(--font-sans)",
  fontSize: 13,
};

function Rail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: "12px 0",
        borderBottom: "1px solid var(--hairline)",
        display: "grid",
        gridTemplateColumns: "80px 1fr",
        alignItems: "center",
        gap: 12,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: "var(--mute)",
        }}
      >
        {label}
      </span>
      <span>{children}</span>
    </div>
  );
}
